import { isShown, isLocked } from '../domain/sceneOps';
import type { Scene, SceneObject } from '../domain/schemas';

/**
 * Pure geometry for the scene editor: camera transforms, hit testing, selection handles
 * and resize/rotate maths. Everything works in *scene* pixels; the camera (zoom/pan) only
 * maps between screen and scene and is never stored in the project.
 */

export interface Point {
  x: number;
  y: number;
}

export interface Camera {
  /** Screen pixels per scene pixel. */
  zoom: number;
  /** Screen position of the scene origin. */
  panX: number;
  panY: number;
}

export const MIN_ZOOM = 0.05;
export const MAX_ZOOM = 8;

export const screenToScene = (c: Camera, p: Point): Point => ({ x: (p.x - c.panX) / c.zoom, y: (p.y - c.panY) / c.zoom });
export const sceneToScreen = (c: Camera, p: Point): Point => ({ x: p.x * c.zoom + c.panX, y: p.y * c.zoom + c.panY });

/** Camera that fits the whole scene in the viewport with a margin, centred. */
export function fitCamera(sceneW: number, sceneH: number, viewW: number, viewH: number, margin = 24): Camera {
  const zoom = clampZoom(Math.min((viewW - margin * 2) / sceneW, (viewH - margin * 2) / sceneH));
  return { zoom, panX: (viewW - sceneW * zoom) / 2, panY: (viewH - sceneH * zoom) / 2 };
}

export const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number.isFinite(z) && z > 0 ? z : 1));

/** Zooms by `factor` keeping the scene point under `screen` fixed. */
export function zoomAt(c: Camera, factor: number, screen: Point): Camera {
  if (!Number.isFinite(factor) || factor <= 0) return c;
  const zoom = clampZoom(c.zoom * factor);
  const anchor = screenToScene(c, screen);
  return { zoom, panX: screen.x - anchor.x * zoom, panY: screen.y - anchor.y * zoom };
}

export const panBy = (c: Camera, dx: number, dy: number): Camera => ({ ...c, panX: c.panX + dx, panY: c.panY + dy });

type Box = Pick<SceneObject, 'x' | 'y' | 'width' | 'height' | 'rotation' | 'anchorX' | 'anchorY'>;

const rad = (deg: number) => (deg * Math.PI) / 180;

/** Scene position of the rotation pivot. */
export const pivotOf = (o: Box): Point => ({ x: o.x + o.anchorX * o.width, y: o.y + o.anchorY * o.height });

/** Maps a point in the object's local box (0…width, 0…height) to scene space. */
export function localToScene(o: Box, p: Point): Point {
  const a = rad(o.rotation);
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const dx = p.x - o.anchorX * o.width;
  const dy = p.y - o.anchorY * o.height;
  const pivot = pivotOf(o);
  return { x: pivot.x + dx * cos - dy * sin, y: pivot.y + dx * sin + dy * cos };
}

/** Maps a scene point into the object's local (unrotated) box coordinates. */
export function sceneToLocal(o: Box, p: Point): Point {
  const a = rad(-o.rotation);
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const pivot = pivotOf(o);
  const dx = p.x - pivot.x;
  const dy = p.y - pivot.y;
  return { x: dx * cos - dy * sin + o.anchorX * o.width, y: dx * sin + dy * cos + o.anchorY * o.height };
}

export const cornersOf = (o: Box): Point[] =>
  [
    { x: 0, y: 0 },
    { x: o.width, y: 0 },
    { x: o.width, y: o.height },
    { x: 0, y: o.height },
  ].map((p) => localToScene(o, p));

/** Axis-aligned bounds of an object after rotation. */
export function boundsOf(o: Box): { x: number; y: number; width: number; height: number } {
  const pts = cornersOf(o);
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

function pointInPolygon(p: Point, pts: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Shape-accurate containment test (ellipses and polygons use their real outline). */
export function containsPoint(o: SceneObject, p: Point): boolean {
  const l = sceneToLocal(o, p);
  if (l.x < 0 || l.y < 0 || l.x > o.width || l.y > o.height) return false;
  switch (o.type) {
    case 'circle':
    case 'ellipse': {
      const nx = (l.x - o.width / 2) / (o.width / 2);
      const ny = (l.y - o.height / 2) / (o.height / 2);
      return nx * nx + ny * ny <= 1;
    }
    case 'polygon':
      return pointInPolygon(l, o.points.map(([x, y]) => ({ x: x * o.width, y: y * o.height })));
    default:
      return true;
  }
}

/**
 * Topmost object under a scene point that the canvas may select: hidden objects and
 * layers are skipped, and so are locked ones (they can still be selected in the panels).
 */
export function hitTest(scene: Scene, p: Point): SceneObject | null {
  for (let li = scene.layers.length - 1; li >= 0; li--) {
    const layer = scene.layers[li]!;
    if (!layer.visible || layer.locked) continue;
    for (let i = scene.objects.length - 1; i >= 0; i--) {
      const o = scene.objects[i]!;
      if (o.layerId !== layer.id || !isShown(scene, o) || isLocked(scene, o)) continue;
      if (containsPoint(o, p)) return o;
    }
  }
  return null;
}

/** Selectable objects whose rotated box intersects a scene rectangle (marquee selection). */
export function objectsInRect(scene: Scene, r: { x: number; y: number; width: number; height: number }): SceneObject[] {
  return scene.objects.filter((o) => {
    if (!isShown(scene, o) || isLocked(scene, o)) return false;
    const b = boundsOf(o);
    return b.x < r.x + r.width && b.x + b.width > r.x && b.y < r.y + r.height && b.y + b.height > r.y;
  });
}

export type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'rotate';

/** Handle positions as fractions of the box. */
export const HANDLE_FRACTIONS: Record<Exclude<Handle, 'rotate'>, [number, number]> = {
  nw: [0, 0],
  n: [0.5, 0],
  ne: [1, 0],
  e: [1, 0.5],
  se: [1, 1],
  s: [0.5, 1],
  sw: [0, 1],
  w: [0, 0.5],
};

/** Distance of the rotate handle above the box, in screen pixels. */
export const ROTATE_OFFSET = 28;
/** Handle hit radius in screen pixels (large enough for touch). */
export const HANDLE_RADIUS = 10;

export function handlePositions(o: Box, zoom: number): Record<Handle, Point> {
  const out = {} as Record<Handle, Point>;
  for (const [h, [fx, fy]] of Object.entries(HANDLE_FRACTIONS) as [Exclude<Handle, 'rotate'>, [number, number]][]) {
    out[h] = localToScene(o, { x: fx * o.width, y: fy * o.height });
  }
  out.rotate = localToScene(o, { x: o.width / 2, y: -ROTATE_OFFSET / zoom });
  return out;
}

/** Which handle (if any) is under a scene point, given the current zoom. */
export function handleAt(o: Box, p: Point, zoom: number): Handle | null {
  const radius = HANDLE_RADIUS / zoom;
  const positions = handlePositions(o, zoom);
  let best: Handle | null = null;
  let bestD = Infinity;
  for (const [h, q] of Object.entries(positions) as [Handle, Point][]) {
    const d = Math.hypot(q.x - p.x, q.y - p.y);
    if (d <= radius && d < bestD) {
      best = h;
      bestD = d;
    }
  }
  return best;
}

/**
 * New box when dragging a resize handle to scene point `p`. The opposite edge/corner
 * stays fixed in scene space (also for rotated objects). `keepAspect` scales uniformly.
 */
export function resizeBox(o: Box, handle: Exclude<Handle, 'rotate'>, p: Point, keepAspect = false, minSize = 1): Pick<SceneObject, 'x' | 'y' | 'width' | 'height'> {
  const [hx, hy] = HANDLE_FRACTIONS[handle];
  const fx = 1 - hx;
  const fy = 1 - hy;
  const fixedLocal = { x: fx * o.width, y: fy * o.height };
  const fixedScene = localToScene(o, fixedLocal);
  const l = sceneToLocal(o, p);
  let w = hx === 0.5 ? o.width : Math.max(minSize, hx === 1 ? l.x - fixedLocal.x : fixedLocal.x - l.x);
  let h = hy === 0.5 ? o.height : Math.max(minSize, hy === 1 ? l.y - fixedLocal.y : fixedLocal.y - l.y);
  if (keepAspect) {
    const ratio = o.width / o.height;
    const s = hx === 0.5 ? h / o.height : hy === 0.5 ? w / o.width : Math.max(w / o.width, h / o.height);
    w = Math.max(minSize, o.width * s);
    h = Math.max(minSize, w / ratio);
  }
  // Solve for the new pivot so the fixed point keeps its scene position.
  const next = { ...o, width: w, height: h };
  const a = rad(o.rotation);
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const dx = fx * w - o.anchorX * w;
  const dy = fy * h - o.anchorY * h;
  const pivot = { x: fixedScene.x - (dx * cos - dy * sin), y: fixedScene.y - (dx * sin + dy * cos) };
  return { x: pivot.x - next.anchorX * w, y: pivot.y - next.anchorY * h, width: w, height: h };
}

/** Rotation (degrees) that points the rotate handle at `p`; snaps to 15° when asked. */
export function rotationTowards(o: Box, p: Point, snap = false): number {
  const pivot = pivotOf(o);
  let deg = (Math.atan2(p.y - pivot.y, p.x - pivot.x) * 180) / Math.PI + 90;
  if (snap) deg = Math.round(deg / 15) * 15;
  return ((((deg + 180) % 360) + 360) % 360) - 180;
}
